// src/pages/HomePage.js
// Homepage styled to match the legacy getsquarefare.com exactly:
// left-aligned hero with orange CTA, beige "What We Help With" icon carousel,
// Google-reviews widget, wavy "How It All Works" band with numbered cards,
// mission/science, newsletter. Login lives in the header (ACCOUNT → Auth0
// popup) and the legacy-site login link is in the footer.

import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { LEGACY, IMG, COLORS, FONTS, HEADING } from 'styles/theme';
import { BUTTON } from 'styles/blocks';
import DELIVERY from 'content/fragments/delivery';
import GoogleReviews from 'components/marketing/GoogleReviews';

const { dark, cream, creamAlt } = COLORS;

// The shared amber button, with the homepage's slightly roomier padding.
const ORANGE_BTN = {
  ...BUTTON, padding: '16px 30px', border: 'none', cursor: 'pointer', letterSpacing: '0.3px',
};

const SERIF_H2 = {
  ...HEADING, fontSize: '44px', color: dark,
  textAlign: 'center', margin: '0 0 40px', lineHeight: 1.15,
};

/* ── What We Help With ─────────────────────────────────────────────────────── */

// The legacy site's own illustrations, in its order. These replaced a set of
// hand-drawn inline SVGs that never matched the brand's line weight.
const HELP_ICONS = `${process.env.PUBLIC_URL || ''}/images/home/help`;

const HELP_ITEMS = [
  { label: 'Weight Loss', icon: `${HELP_ICONS}/weight-loss.webp` },
  { label: 'Muscle Gain', icon: `${HELP_ICONS}/muscle-gain.webp` },
  { label: 'Bloating', icon: `${HELP_ICONS}/bloating.webp` },
  { label: 'Fatigue & Stress', icon: `${HELP_ICONS}/fatigue-stress.webp` },
  { label: 'Skin, Nail, & Hair Health', icon: `${HELP_ICONS}/skin-hair-nails.webp` },
  { label: 'Pre & Post Partum Care', icon: `${HELP_ICONS}/pre-post-partum.webp` },
  { label: 'Prediabetes & Type 2 Diabetes', icon: `${HELP_ICONS}/type-2-diabetes.webp` },
  { label: 'Building Better Habits', icon: `${HELP_ICONS}/better-habits.webp` },
];

function HelpCarousel() {
  const trackRef = useRef(null);
  const scroll = (dir) => trackRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });

  const arrowStyle = {
    width: '54px', height: '54px', borderRadius: '50%', background: '#8f8f93',
    border: 'none', cursor: 'pointer', color: '#fff', fontSize: '22px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '18px', maxWidth: '1240px', margin: '0 auto' }}>
      <button style={arrowStyle} onClick={() => scroll(-1)} aria-label="Previous">←</button>
      <div ref={trackRef} style={{
        display: 'flex', gap: '30px', overflowX: 'auto', scrollbarWidth: 'none',
        flex: 1, padding: '10px 4px',
      }}>
        {HELP_ITEMS.map(item => (
          <div key={item.label} style={{ minWidth: '215px', textAlign: 'center' }}>
            <img
              src={item.icon}
              alt=""
              loading="lazy"
              style={{ width: '104px', height: '104px', display: 'block', margin: '0 auto 18px' }}
            />
            <div style={{ ...HEADING, fontSize: '25px', color: dark, lineHeight: 1.3 }}>
              {item.label}
            </div>
          </div>
        ))}
      </div>
      <button style={arrowStyle} onClick={() => scroll(1)} aria-label="Next">→</button>
    </div>
  );
}


function WavyBand() {
  const phrase = 'How It All Works   •   ';
  const track = phrase.repeat(6);
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  return (
    <div aria-hidden="true" style={{ overflow: 'hidden', background: cream }}>
      <svg viewBox="0 0 1440 190" style={{ display: 'block', width: '100%' }}>
        <defs>
          <path id="sf-wave" d="M -60 120 C 180 30, 420 190, 660 105 C 900 25, 1140 190, 1500 95" fill="none" />
        </defs>
        <text style={{ ...HEADING, fontSize: '58px', fill: dark }}>
          <textPath href="#sf-wave" startOffset="0%">
            {track}
            {!reduceMotion && (
              <animate
                attributeName="startOffset"
                from="0%"
                to="-100%"
                dur="15s"
                repeatCount="indefinite"
              />
            )}
          </textPath>
        </text>
      </svg>
    </div>
  );
}

const stepLink = { color: 'inherit', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' };

// Size and gap for the step-number badge below each photo.
const STEP_BADGE_SIZE = 64;
const STEP_BADGE_GAP = 20;

// Each step carries a photo. The three steps used to be flat beige boxes with a
// numbered circle, which is the "corporate" look Claire flagged — the content is
// identical, it just leads with food now.
const STEPS = [
  {
    n: '1', title: 'Pick a Plan', img: IMG.htoStep1,
    body: (
      <>
        <strong>Select <a href={`${LEGACY}/prepared-meal-subscription`} style={stepLink}>a plan</a> that works for your
        lifestyle.</strong> If you’d rather <Link to="/this-weeks-menu" style={stepLink}><strong>buy a la carte</strong></Link>,
        you can do that too!
      </>
    ),
  },
  {
    n: '2', title: 'Take Our Quiz', img: IMG.htoStep2,
    body: (
      <>
        Take our <Link to="/quiz" style={stepLink}><strong>3-minute</strong></Link> lifestyle quiz! This information
        is used to <strong>tailor our menu to fit your specific needs.</strong>
      </>
    ),
  },
  {
    n: '3', title: 'Feel Good!', img: IMG.htoStep3,
    body: (
      <>
        <strong>We do the research and cooking so you don’t have to.</strong> All meals come ready to eat, just pop
        in the microwave if needed and enjoy.
      </>
    ),
  },
];

/* ── Page ──────────────────────────────────────────────────────────────────── */

export default function HomePage() {
  return (
    <div style={{ fontFamily: FONTS.sans, background: cream }}>

      {/* Hero — left-aligned, exactly like legacy */}
      <section style={{
        position: 'relative', minHeight: '82vh', display: 'flex', alignItems: 'center',
        backgroundImage: `linear-gradient(rgba(30,28,26,0.35), rgba(30,28,26,0.35)), url(${IMG.heroChef})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
      }}>
        <div style={{ maxWidth: '640px', padding: '80px 4%', color: '#fff' }}>
          <h1 style={{
            ...HEADING, fontSize: '58px', lineHeight: 1.1, margin: '0 0 28px',
          }}>
            Freshly–made, tailored to YOU.
          </h1>
          <p style={{ fontSize: '21px', lineHeight: 1.75, margin: '0 0 42px' }}>
            Personalized ready-to-eat meals based on your specific nutritional needs- delivered to your door!
            Starting at $16.99 / meal. Never frozen, locally-sourced. Gluten-free, no seed oils, no added sugar.
          </p>
          <Link to="/quiz" style={ORANGE_BTN}>Take the Quiz</Link>
        </div>
      </section>

      {/* Delivery info band — between the hero and the photo strip, like legacy */}
      <section style={{ background: creamAlt, padding: '64px 4%' }}>
        <div style={{
          maxWidth: '1240px', margin: '0 auto', display: 'grid', gap: '40px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', alignItems: 'start',
        }}>
          <p style={{ fontSize: '18px', lineHeight: 1.8, color: dark, margin: 0 }}>
            Deliveries:&nbsp; <strong>{DELIVERY.short}</strong>
          </p>
          <div>
            <p style={{ fontSize: '18px', lineHeight: 1.8, color: dark, margin: '0 0 40px' }}>
              Currently serving {DELIVERY.zones}.
            </p>
            <p style={{ fontSize: '18px', lineHeight: 1.8, color: dark, fontWeight: 700, margin: 0 }}>
              Looking for a different delivery time or location?{' '}
              <Link to="/contact-form-general" style={{
                color: 'inherit', fontStyle: 'italic', textDecoration: 'underline', textUnderlineOffset: '3px',
              }}>Contact us</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Photo strip curving into the section below. Claire asked for the food
          photography to carry the page, so this is a full band rather than the
          thin sliver it was — the curve fill has to match whatever follows. */}
      <div style={{ position: 'relative', height: '460px', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${IMG.menu})`, backgroundSize: 'cover', backgroundPosition: 'center',
        }} />
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ position: 'absolute', bottom: -1, left: 0, width: '100%', height: '120px' }}>
          <path d="M0,120 L0,55 C480,130 960,130 1440,55 L1440,120 Z" fill={cream} />
        </svg>
      </div>

      {/* This Week's Menu — above What We Help With, and showing actual food.
          It previously had no imagery at all, which was the main reason the page
          read as corporate: a menu section with nothing to look at. */}
      <section style={{ background: cream, padding: '70px 4% 80px', textAlign: 'center' }}>
        <h2 style={{ ...SERIF_H2, marginBottom: '14px' }}>This Week's Menu</h2>
        <p style={{ fontSize: '17px', color: COLORS.text, margin: '0 0 38px', lineHeight: 1.7 }}>
          Our menu changes weekly — new flavors, seasonal ingredients, portioned to your needs.
        </p>
        <div style={{
          maxWidth: '1240px', margin: '0 auto 40px', display: 'grid', gap: '18px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        }}>
          {[IMG.htoStep1, IMG.homeVeggies, IMG.htoStep3].map((src) => (
            <div key={src} style={{
              height: '340px', backgroundImage: `url(${src})`,
              backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: '10px',
            }} />
          ))}
        </div>
        <Link to="/this-weeks-menu" style={ORANGE_BTN}>See the Menu</Link>
      </section>

      {/* What We Help With */}
      <section style={{ background: creamAlt, padding: '70px 4%' }}>
        <h2 style={SERIF_H2}>What We Help With</h2>
        <HelpCarousel />
        <div style={{ textAlign: 'center', marginTop: '44px' }}>
          <Link to="/health-goals" style={ORANGE_BTN}>Learn More</Link>
        </div>
      </section>

      {/* Reviews */}
      <GoogleReviews />

      {/* How It All Works */}
      <WavyBand />
      <section style={{ background: cream, padding: '0 4% 80px' }}>
        <div style={{
          maxWidth: '1240px', margin: '0 auto', display: 'grid', gap: '26px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        }}>
          {STEPS.map(s => (
            <div key={s.n} style={{ textAlign: 'center' }}>
              <div style={{ position: 'relative', marginBottom: `${2 * STEP_BADGE_GAP + STEP_BADGE_SIZE}px` }}>
                <div style={{
                  height: '300px', borderRadius: '10px', overflow: 'hidden',
                  backgroundImage: `url(${s.img})`, backgroundSize: 'cover', backgroundPosition: 'center',
                }} />
                {/* STEP_BADGE_GAP below the photo, matching the gap kept above the title. */}
                <div style={{
                  position: 'absolute', top: `calc(100% + ${STEP_BADGE_GAP}px)`, left: '50%', transform: 'translate(-50%, 0)',
                  width: `${STEP_BADGE_SIZE}px`, height: `${STEP_BADGE_SIZE}px`, borderRadius: '50%', background: dark, color: '#fff',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  ...HEADING, fontSize: '30px',
                }}>{s.n}</div>
              </div>
              <h3 style={{ ...HEADING, fontSize: '32px', color: dark, margin: '0 0 18px' }}>
                {s.title}
              </h3>
              <p style={{ fontSize: '18px', lineHeight: 1.8, color: COLORS.text, margin: 0 }}>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mission */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', alignItems: 'center', background: '#fff' }}>
        <div style={{ minHeight: '560px', backgroundImage: `url(${IMG.mission})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div style={{ padding: '60px 7%' }}>
          <h2 style={{ ...HEADING, fontSize: '38px', color: dark, margin: '0 0 18px' }}>Our Mission</h2>
          <p style={{ fontSize: '17px', lineHeight: 1.8, color: COLORS.text, margin: '0 0 26px' }}>
            We help you achieve your health and fitness goals in a sustainable way. Our fresh meals are
            delicious, home-made, and convenient — rooted in science and tailored to your needs, no matter
            your dietary preferences.
          </p>
          <Link to="/about" style={ORANGE_BTN}>Learn More</Link>
        </div>
      </section>

      {/* Science */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', alignItems: 'center', background: creamAlt }}>
        <div style={{ padding: '60px 7%', order: 1 }}>
          <h2 style={{ ...HEADING, fontSize: '38px', color: dark, margin: '0 0 18px' }}>The Science</h2>
          <p style={{ fontSize: '17px', lineHeight: 1.8, color: COLORS.text, margin: '0 0 26px' }}>
            There's an extensive analytical process behind each meal. Our experts analyze your calorie,
            macro, and micronutrient requirements alongside your preferences and allergies to calculate
            the perfect portions — a truly personalized, health-focused dining experience.
          </p>
          <Link to="/science" style={ORANGE_BTN}>Learn More</Link>
        </div>
        <div style={{ minHeight: '560px', backgroundImage: `url(${IMG.science})`, backgroundSize: 'cover', backgroundPosition: 'center', order: 2 }} />
      </section>

      {/* Find Us On Instagram — Elfsight live feed widget, replacing the old static tile grid */}
      <section style={{ background: cream, padding: '70px 4%' }}>
        <h2 style={SERIF_H2}>Find Us On Instagram</h2>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-43e67abf-b274-46ee-8e14-2c7c37d4ed20" data-elfsight-app-lazy></div>
      </section>

      {/* The "Stay Updated" band the footer also carries — see NewsletterSignup. */}
    </div>
  );
}
